/******************************************************************************
 *
 * Copyright (c) 2019-2020 ServiceActivator Inc. All Rights Reserved.
 *
 * Unauthorized copying of the code in this file, completely or partially,
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 *
 * Written by 'Vishal Goel’ <vishal@wakencode.com>, February 2020
 *
 *****************************************************************************/

import * as actionTypes from "./actionTypes";
import { axiosSA } from "../../axios";
import { processGeometryData } from "../../shared/utlilty";
import { toast } from "react-toastify";
import { constant } from "./../../shared/constants/constants";
import moment from "moment";
import { setToken } from "./index";
import { convertPropertiesV1toV0 } from "../../shared/helper";

// fetch todays geometry
export const fetchTodaysGeometry = (showAllProperties) => {
  return async (dispatch) => {
    let momentInstance = moment();

    // current visit window date
    let date =
      momentInstance.hour() >= 18
        ? momentInstance.add(1, "d").format("YYYYMMDD")
        : momentInstance.format("YYYYMMDD");

    // clear alreadyPresent geometry
    dispatch(clearTodaysGeometry());
    dispatch(fetchTodaysGeometryHelper(showAllProperties, date));
  };
};

// fetch todays geometry helper method
const fetchTodaysGeometryHelper = (
  showAllProperties,
  date,
  currentPage = 1
) => {
  return async (dispatch) => {
    try {
      let token = await dispatch(setToken());
      let params = {
        pageSize: 200,
        pageNo: currentPage,
        allowInactive: false,
        isFetchGeometries: true,
      };
      if (!showAllProperties) params.workOrderDate = date;

      const firstResponse = await axiosSA.get(`/properties/list`, {
        params: params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // get meta data from the first response
      const meta = firstResponse.data.meta.page;
      const lastPage = meta.lastPage;

      // process the first response and set data in store
      const firstPageData = convertPropertiesV1toV0(firstResponse.data.data);
      // dispatch(setTodaysGeometry(processGeometryData(firstPageData)));

      const requests = []; // array to store axios requests

      // create multiple axios requests for parallel API calls
      for (let i = currentPage + 1; i <= lastPage; i++) {
        const request = axiosSA.get(`/properties/list`, {
          params: { ...params, pageNo: i },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        requests.push(request);
      }

      // Display loading toast message while requests are pending
      toast.info('Fetching properties data...');

      // execute API calls in parallel using Promise.allSettled()
      const responses = await Promise.allSettled(requests);

      // process responses and set data in store
      const geometries = [];
      const failedRequests = [];

      responses.forEach((res) => {
        if (res.status === 'fulfilled') {
          const data = convertPropertiesV1toV0(res.value.data.data);
          geometries.push(data);
        } else {
          failedRequests.push(res.reason); // save the reason/error message for failed requests
        }
      });

      if (failedRequests.length > 0) {
        // Display error messages for failed requests
        failedRequests.forEach((error) => {
          console.error(error);
          toast.error(error); // Display the error message using toast or any other suitable method
        });
      }

      const mergedGeometries = firstPageData.concat(...geometries);
      dispatch(setTodaysGeometry(processGeometryData(mergedGeometries)));

      // Hide the loading toast message
      toast.dismiss();
    } catch (err) {
      toast.error(constant.SOME_ERROR_OCCURRED_IN_FETCHING_PROPERTIES);
    }
  };
};

// fetch selected dates geometry
export const fetchFilteredGeometry = (dates) => {
  return (dispatch) => {
    // clear alreadyPresent filteredGeometry
    dispatch(clearFilteredGeometry());
    for (let date of dates) {
      // fetch geometry for each date parallelly
      dispatch(fetchFilteredGeometryHelper(date));
    }
  };
};

// fetch selected dates geometry helper method
const fetchFilteredGeometryHelper = (date, currentPage = 1) => {
  return async (dispatch) => {
    let token = await dispatch(setToken());
    axiosSA
      .get(`/properties/list`, {
        params: {
          pageSize: 100,
          pageNo: currentPage,
          allowInactive: true,
          workOrderDate: date,
          isFetchGeometries: true,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        // set processGeometryData in store
        dispatch(
          setFilteredGeometry(
            processGeometryData(convertPropertiesV1toV0(res.data.data))
          )
        );

        // increment current page count
        currentPage += 1;
        let lastPage = res.data.meta.page.lastPage;

        // if current page count is greater than last page count return
        // else recursively call method with incremented page count
        if (currentPage > lastPage) return;
        dispatch(fetchFilteredGeometryHelper(date, currentPage));
      })
      .catch((err) => {
        toast.error(constant.SOME_ERROR_OCCURRED_IN_FETCHING_PROPERTIES);
      });
  };
};

// set todays fetched geometry in redux store
const setTodaysGeometry = (data) => {
  return {
    type: actionTypes.SET_TODAYS_GEOMETRY,
    data,
  };
};

// set filtered fetched geometry in redux store
const setFilteredGeometry = (data) => {
  return {
    type: actionTypes.SET_FILTERED_GEOMETRY,
    data,
  };
};

// clear todays geometry array in redux store
export const clearTodaysGeometry = () => {
  return {
    type: actionTypes.CLEAR_TODAYS_GEOMETRY,
  };
};

// clear filtered geometry array in redux store
export const clearFilteredGeometry = () => {
  return {
    type: actionTypes.CLEAR_FILTERED_GEOMETRY,
  };
};
